import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { PartyService } from '../party.service';
import { Party } from '../party.model';
import { CompanyService } from 'src/app/company/company.service';
import { Company } from 'src/app/company/company.model';
import { PartyType } from '../party-type.enum';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { PartyTableComponent } from '../party-table/party-table.component';
import { TabsModule } from 'primeng/tabs';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { PageHeroComponent } from 'src/app/shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { PartyQuickCreateDialogComponent, PartyQuickCreateMode } from 'src/app/shared/party-quick-create/party-quick-create-dialog.component';
import { TransportationAgency } from 'src/app/company/transportation-agency/transportation-agency.model';
import { TransportationAgencyService } from 'src/app/company/transportation-agency/transportation-agency.service';
import { FreightForwarder } from 'src/app/company/freight-forwarder/freight-forwarder.model';
import { FreightForwarderService } from 'src/app/company/freight-forwarder/freight-forwarder.service';
import { CustomsClearanceEndpointService } from 'src/app/clearance/clearance.service';
import { Shipper } from 'src/app/clearance/models/shipper.model';

const entityTypePropertyMap: { [key: string]: { data: string, total: string } } = {
    Company: { data: 'companies', total: 'totalCompanies' },
    TransportationAgency: { data: 'agencies', total: 'totalAgencies' },
    FreightForwarder: { data: 'forwarders', total: 'totalForwarders' },
    Shipper: { data: 'shippers', total: 'totalShippers' }
};

@Component({
    selector: 'app-clients',
    templateUrl: './clients.component.html',
    styleUrls: ['./clients.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ToastModule,
        TabsModule,
        ButtonModule,
        InputTextModule,
        DialogModule,
        PartyTableComponent,
        PartyQuickCreateDialogComponent,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ],
    providers: [
        MessageService,
        CompanyService
    ],
})
export class ClientsComponent implements OnInit {
    private readonly tabKeys = ['companies', 'agencies', 'forwarders', 'shippers'] as const;
    @Input() searchText = '';

    @ViewChild('companyTable') companyTable: PartyTableComponent;
    @ViewChild('agencyTable') agencyTable: PartyTableComponent;
    @ViewChild('forwarderTable') forwarderTable: PartyTableComponent;
    @ViewChild('shipperTable') shipperTable: PartyTableComponent;

    companies: Party[] = [];
    agencies: Party[] = [];
    forwarders: Party[] = [];
    shippers: Party[] = [];

    totalCompanies = 0;
    totalAgencies = 0;
    totalForwarders = 0;
    totalShippers = 0;

    isLoading = false;
    activeTabIndex = 0;
    searchTerms: Record<string, string> = {
        Company: '',
        TransportationAgency: '',
        FreightForwarder: '',
        Shipper: ''
    };
    owningCompanyId: number;

    quickCreateVisible = false;
    quickCreateMode: PartyQuickCreateMode | null = null;
    quickCreateRecord: Company | TransportationAgency | FreightForwarder | Shipper | null = null;
    quickCreateTitle: string | null = null;
    quickCreateSubmitLabel: string | null = null;
    companyLookupVisible = false;
    companyLookupCrNumber = '';
    companyLookupSubmitting = false;

    constructor(
        private partyService: PartyService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private companyService: CompanyService,
        private transportationAgencyService: TransportationAgencyService,
        private freightForwarderService: FreightForwarderService,
        private clearanceService: CustomsClearanceEndpointService,
        private route: ActivatedRoute,
        private router: Router
    ) {
        this.owningCompanyId = this.sessionStorageService.getCompany()?.id ?? 0;
    }

    ngOnInit(): void {
        this.route.queryParamMap.subscribe(params => {
            this.activeTabIndex = this.resolveTabIndex(params.get('tab'));
        });
    }

    get owningCompany(): Company | null {
        return this.sessionStorageService.getCompany() ?? null;
    }

    get countries() {
        return this.sessionStorageService.getCountryList() ?? [];
    }

    readonly organizationDetailRoute = (party: Party): Array<string | number> | null =>
        party?.id?.entityType === PartyType.Company && party?.id?.entityId
            ? ['/company', party.id.entityId]
            : null;

    createParty(partyType: string): void {
        const mode = this.modeForClientType(partyType);
        if (!mode) {
            this.messageService.add({ severity: 'warn', summary: 'Unavailable', detail: `${partyType} organizations cannot be created from this workspace.` });
            return;
        }

        if (partyType === PartyType.Company) {
            this.openCompanyLookup();
            return;
        }

        this.quickCreateMode = mode;
        this.quickCreateRecord = this.emptyRecordForMode(mode);
        this.quickCreateTitle = this.dialogTitleForClientType(partyType, false);
        this.quickCreateSubmitLabel = 'Create';
        this.quickCreateVisible = true;
    }

    loadData(event: TableLazyLoadEvent, entityTypes: string[]): void {
        this.isLoading = true;
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            this.isLoading = false;
            return;
        }

        const page = (event.first ?? 0) / (event.rows ?? 10);
        const size = event.rows ?? 10;
        const searchText = this.getSearchText(entityTypes[0]);

        this.partyService.getParties(companyId, entityTypes, searchText, page, size)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (data) => {
                    const entityType = entityTypes[0];
                    const propertyNames = entityTypePropertyMap[entityType];
                    if (propertyNames) {
                        (this as any)[propertyNames.data] = data.content;
                        (this as any)[propertyNames.total] = data.totalElements;
                    }
                },
                error: (err) => {
                    console.error('Failed to load party data:', err);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Data Load Failed',
                        detail: 'Could not retrieve data. Please try again.'
                    });
                }
            });
    }

    reloadDataForActiveTab(): void {
        const tables = [this.companyTable, this.agencyTable, this.forwarderTable, this.shipperTable];
        tables[this.activeTabIndex]?.reset();
    }

    onTabChange(event: { index: number | string }): void {
        this.activeTabIndex = event.index as number;
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: this.tabKeys[this.activeTabIndex] || this.tabKeys[0] },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    onSearch(): void {
        this.reloadDataForActiveTab();
    }

    clearSearch(entityType: string): void {
        this.searchTerms[entityType] = '';
        this.onSearch();
    }

    getSearchText(entityType: string): string {
        return (this.searchTerms[entityType] ?? this.searchText ?? '').trim();
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Companies', value: this.totalCompanies, hint: 'Linked organizations', tone: 'info' },
            { label: 'Agencies', value: this.totalAgencies, hint: 'Transport partners', tone: 'success' },
            { label: 'Forwarders', value: this.totalForwarders, hint: 'Freight partners', tone: 'warn' },
            { label: 'Shippers', value: this.totalShippers, hint: 'Origin parties', tone: 'info' }
        ];
    }

    onQuickCreateCompleted(): void {
        this.quickCreateVisible = false;
        this.reloadDataForActiveTab();
    }

    onQuickCreateVisibilityChanged(visible: boolean): void {
        this.quickCreateVisible = visible;
        if (!visible) {
            this.resetQuickCreateState();
        }
    }

    onQuickCreateFailed(message: string): void {
        this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: message || 'Unable to save organization.' });
    }

    closeCompanyLookup(): void {
        this.companyLookupVisible = false;
        this.companyLookupCrNumber = '';
        this.companyLookupSubmitting = false;
    }

    submitCompanyLookup(): void {
        const normalizedCrNumber = this.normalizedCompanyLookupCrNumber();
        if (!normalizedCrNumber || !this.owningCompany?.id) {
            return;
        }

        this.companyLookupSubmitting = true;
        this.partyService.linkCompanyByCrNumber(this.owningCompany.id, normalizedCrNumber)
            .pipe(finalize(() => this.companyLookupSubmitting = false))
            .subscribe({
                next: resolution => {
                    if (resolution.status === 'NOT_FOUND') {
                        this.closeCompanyLookup();
                        const company = new Company();
                        company.crNumber = Number(normalizedCrNumber);
                        this.quickCreateMode = 'company';
                        this.quickCreateRecord = company;
                        this.quickCreateTitle = 'Create Company';
                        this.quickCreateSubmitLabel = 'Create';
                        this.quickCreateVisible = true;
                        return;
                    }

                    const companyName = resolution.party?.entityName || 'Company';
                    this.messageService.add({
                        severity: resolution.status === 'ALREADY_LINKED' ? 'info' : 'success',
                        summary: resolution.status === 'ALREADY_LINKED' ? 'Already Linked' : 'Linked',
                        detail: resolution.status === 'ALREADY_LINKED'
                            ? `${companyName} is already linked to this tenant.`
                            : `${companyName} was linked successfully.`
                    });
                    this.closeCompanyLookup();
                    this.companyTable?.reset();
                },
                error: err => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Lookup Failed',
                        detail: err?.error?.message || err?.message || 'Unable to check the CR number.'
                    });
                }
            });
    }

    editParty(party: Party): void {
        switch (party.id.entityType) {
            case PartyType.Company:
                this.loadCompany(party.id.entityId);
                break;
            case PartyType.TransportationAgency:
                this.loadTransportationAgency(party.id.entityId);
                break;
            case PartyType.FreightForwarder:
                this.loadFreightForwarder(party.id.entityId);
                break;
            case PartyType.Shipper:
                this.loadShipper(party.id.entityId);
                break;
            default:
                this.messageService.add({ severity: 'info', summary: 'Info', detail: 'A detail page is not available for this organization type yet.' });
                break;
        }
    }

    private loadCompany(entityId: number): void {
        this.isLoading = true;
        this.companyService.getCompanyById(entityId)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (response) => {
                    if (!response.data) {
                        this.messageService.add({ severity: 'error', summary: 'Not Found', detail: 'Company details could not be loaded.' });
                        return;
                    }
                    this.quickCreateMode = 'company';
                    this.quickCreateRecord = response.data;
                    this.quickCreateTitle = 'Edit Company';
                    this.quickCreateSubmitLabel = 'Update';
                    this.quickCreateVisible = true;
                },
                error: (err) => {
                    this.messageService.add({ severity: 'error', summary: 'API Error', detail: 'Failed to load company data: ' + (err.error?.message || err.message) });
                }
            });
    }

    private loadTransportationAgency(entityId: number): void {
        this.isLoading = true;
        this.transportationAgencyService.findTransportationAgencyByFilter(undefined, undefined, entityId)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (response) => {
                    if (!response.data) {
                        this.messageService.add({ severity: 'error', summary: 'Not Found', detail: 'Transportation agency details could not be loaded.' });
                        return;
                    }
                    this.quickCreateMode = 'transportationAgency';
                    this.quickCreateRecord = response.data;
                    this.quickCreateTitle = 'Edit Transportation Agency';
                    this.quickCreateSubmitLabel = 'Update';
                    this.quickCreateVisible = true;
                },
                error: (err) => {
                    this.messageService.add({ severity: 'error', summary: 'API Error', detail: 'Failed to load transportation agency: ' + (err.error?.message || err.message) });
                }
            });
    }

    private loadFreightForwarder(entityId: number): void {
        this.isLoading = true;
        this.freightForwarderService.getFreightForwarder(undefined, entityId)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (response) => {
                    if (!response) {
                        this.messageService.add({ severity: 'error', summary: 'Not Found', detail: 'Freight forwarder details could not be loaded.' });
                        return;
                    }
                    this.quickCreateMode = 'broker';
                    this.quickCreateRecord = response;
                    this.quickCreateTitle = 'Edit Freight Forwarder';
                    this.quickCreateSubmitLabel = 'Update';
                    this.quickCreateVisible = true;
                },
                error: (err) => {
                    this.messageService.add({ severity: 'error', summary: 'API Error', detail: 'Failed to load freight forwarder: ' + (err.error?.message || err.message) });
                }
            });
    }

    private loadShipper(entityId: number): void {
        this.isLoading = true;
        this.clearanceService.getShipperById(entityId)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (response) => {
                    if (!response) {
                        this.messageService.add({ severity: 'error', summary: 'Not Found', detail: 'Shipper details could not be loaded.' });
                        return;
                    }
                    this.quickCreateMode = 'shipper';
                    this.quickCreateRecord = response;
                    this.quickCreateTitle = 'Edit Shipper';
                    this.quickCreateSubmitLabel = 'Update';
                    this.quickCreateVisible = true;
                },
                error: (err) => {
                    this.messageService.add({ severity: 'error', summary: 'API Error', detail: 'Failed to load shipper: ' + (err.error?.message || err.message) });
                }
            });
    }

    private modeForClientType(partyType: string): PartyQuickCreateMode | null {
        switch (partyType) {
            case PartyType.Company:
                return 'company';
            case PartyType.TransportationAgency:
                return 'transportationAgency';
            case PartyType.FreightForwarder:
                return 'broker';
            case PartyType.Shipper:
                return 'shipper';
            default:
                return null;
        }
    }

    private dialogTitleForClientType(partyType: string, editing: boolean): string {
        const action = editing ? 'Edit' : 'Create';
        switch (partyType) {
            case PartyType.Company:
                return `${action} Company`;
            case PartyType.TransportationAgency:
                return `${action} Transportation Agency`;
            case PartyType.FreightForwarder:
                return `${action} Freight Forwarder`;
            case PartyType.Shipper:
                return `${action} Shipper`;
            default:
                return action;
        }
    }

    private emptyRecordForMode(mode: PartyQuickCreateMode): Company | TransportationAgency | FreightForwarder | Shipper {
        switch (mode) {
            case 'company':
                return new Company();
            case 'transportationAgency':
                return new TransportationAgency();
            case 'broker':
                return new FreightForwarder();
            case 'shipper':
                return new Shipper('', this.owningCompanyId);
            default:
                return new Company();
        }
    }

    private resetQuickCreateState(): void {
        this.quickCreateMode = null;
        this.quickCreateRecord = null;
        this.quickCreateTitle = null;
        this.quickCreateSubmitLabel = null;
    }

    private openCompanyLookup(): void {
        this.companyLookupCrNumber = '';
        this.companyLookupVisible = true;
    }

    private normalizedCompanyLookupCrNumber(): string {
        return this.companyLookupCrNumber.replace(/\D/g, '').trim();
    }

    private resolveTabIndex(tab: string | null): number {
        const index = this.tabKeys.findIndex(value => value === (tab || '').toLowerCase());
        return index >= 0 ? index : 0;
    }
}
